import { useId, useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../api/client";
import type { PmProject } from "../api/pm";
import type {
  ImputationHints,
  ProjectSuggestion,
  RuleMatchField,
  UnassignedActivity,
} from "../api/types";
import {
  invalidateReimputationReads,
  useReimputeActivities,
  useReimputeActivity,
} from "../hooks/useActivities";
import { useCreateImputationRule } from "../hooks/useImputationRules";
import { useProjects, useProjectWorkItems } from "../hooks/usePm";
import { useUnassignedTime } from "../hooks/useUnassignedTime";
import { useAdminScope } from "../lib/auth/useGrantedScope";
import { claimableActivities } from "../lib/autoAttach";
import { lastSegment, TRUNK_BRANCHES } from "../lib/dayFrise";
import { formatMinutes, plural } from "../lib/format";
import {
  createRuleAndApply,
  ruleFieldOf,
  type RuleApplied,
} from "../lib/imputationRules";
import { useUiStore } from "../state/uiStore";
import { ProjectFormSheet } from "./ProjectFormSheet";
import {
  DEFAULT_REIMPUTE_REASON,
  ReimputeSheet,
  reimputeErrorMessage,
  type ReimputeTarget,
} from "./ReimputeSheet";
import { Sheet } from "./Sheet";

const LABEL_MAX = 200;
/** Le rattachement s'applique aussi au temps passé, un an en arrière. */
const APPLY_WINDOWS = 1;
const MAX_BATCH = 200;

/** Ce qu'on rattache : un site, un dépôt, ou un indice du kit (`field`
 * l'indique alors, `title` le nomme). */
export interface AttachTarget {
  kind: "domain" | "code" | "none";
  value: string;
  branch?: string;
  minutes: number;
  field?: RuleMatchField;
  title?: string;
}

interface Source extends AttachTarget {
  key: string;
  eventIds: string[];
  activities: UnassignedActivity[];
}

export interface RetainedHint {
  field: RuleMatchField;
  value: string;
  branch?: string;
}

interface ProjectRef {
  project_id: string;
  work_item_id?: string;
  project_label?: string;
}

/** Réimpute des créneaux à un projet, d'un appel unitaire ou groupé. Une clé
 * d'idempotence par geste : un second choix après un refus n'est pas pris
 * pour un rejeu. */
function useReimputeEvents() {
  const one = useReimputeActivity();
  const many = useReimputeActivities();
  async function run(eventIds: string[], ref: ProjectRef) {
    const ids = eventIds.slice(0, MAX_BATCH);
    const envelope = {
      reason: DEFAULT_REIMPUTE_REASON,
      correlation_id: crypto.randomUUID(),
    };
    if (ids.length === 1) {
      await one.mutateAsync({ startEventId: ids[0]!, ...ref, ...envelope });
    } else {
      await many.mutateAsync({
        ...envelope,
        items: ids.map((event_id) => ({ event_id, ...ref })),
      });
    }
  }
  return { run, pending: one.isPending || many.isPending };
}

/** Retient un indice : règle pour soi, appliquée au temps passé. Un échec
 * se dit sans défaire l'imputation déjà faite. */
function useRetainHint(workerUrl: string) {
  const create = useCreateImputationRule();
  const pushToast = useUiStore((s) => s.pushToast);
  return async (hint: RetainedHint, ref: ProjectRef) => {
    try {
      await createRuleAndApply(
        create.mutateAsync,
        {
          worker_url: workerUrl,
          match_field: hint.field,
          match_value: hint.value,
          ...(hint.branch && !TRUNK_BRANCHES.has(hint.branch)
            ? { branch_pattern: hint.branch }
            : {}),
          ...ref,
        },
        APPLY_WINDOWS,
      );
    } catch (err) {
      pushToast({
        variant: "error",
        title: "Règle non retenue",
        subtitle:
          err instanceof ApiError && err.status === 409
            ? "Une règle existe déjà pour cet indice."
            : "Le temps est imputé ; retenez l'indice depuis Règles d'imputation.",
      });
    }
  };
}

function suggestionRef(sp: ProjectSuggestion): ProjectRef {
  return {
    project_id: sp.project_id,
    ...(sp.work_item_id ? { work_item_id: sp.work_item_id } : {}),
    ...(sp.project_label
      ? { project_label: sp.project_label.slice(0, LABEL_MAX) }
      : {}),
  };
}

/** Confirme un projet suggéré d'un geste, et retient l'indice si demandé. */
export function useConfirmSuggestion(workerUrl: string) {
  const [error, setError] = useState<string | null>(null);
  const [retaining, setRetaining] = useState(false);
  const reimpute = useReimputeEvents();
  const retainHint = useRetainHint(workerUrl);
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);
  const pending = reimpute.pending || retaining;

  async function confirm({
    suggestion,
    eventIds,
    minutes,
    retain,
  }: {
    suggestion: ProjectSuggestion;
    eventIds: string[];
    minutes: number;
    retain?: RetainedHint;
  }) {
    if (pending) return;
    const ref = suggestionRef(suggestion);
    const label = suggestion.project_label || "un projet déjà utilisé";
    setError(null);
    try {
      await reimpute.run(eventIds, ref);
    } catch (err) {
      setError(reimputeErrorMessage(err));
      return;
    }
    pushToast({
      variant: "info",
      title: `${formatMinutes(minutes)} imputées à ${label}`,
    });
    if (retain) {
      setRetaining(true);
      await retainHint(retain, ref);
      setRetaining(false);
      invalidateReimputationReads(qc);
    }
  }

  return { confirm, pending, error };
}

/** D'où vient le temps sans projet : le site (domaine), le dépôt et sa
 * branche, ou rien. C'est ce que compare une règle d'imputation. */
function sourcesOf(activities: UnassignedActivity[]): Source[] {
  const byKey = new Map<string, Source>();
  for (const a of activities) {
    const code = a.context_repo || a.context_project;
    const kind = a.context_domain ? "domain" : code ? "code" : "none";
    const value = a.context_domain || code || "";
    const branch = kind === "code" ? a.context_branch || undefined : undefined;
    const key = `${kind}:${value}:${branch ?? ""}`;
    const s = byKey.get(key) ?? {
      key,
      kind,
      value,
      ...(branch ? { branch } : {}),
      minutes: 0,
      eventIds: [],
      activities: [],
    };
    s.minutes += a.worked_minutes;
    s.activities.push(a);
    if (!s.eventIds.includes(a.event_id)) s.eventIds.push(a.event_id);
    byKey.set(key, s);
  }
  return [...byKey.values()].sort((x, y) => y.minutes - x.minutes);
}

function sourceTitle(s: AttachTarget): string {
  if (s.title) return s.title;
  if (s.kind === "none") return "Sans source connue";
  return s.kind === "domain" ? `Site ${s.value}` : s.value;
}

/** Indices lisibles : dossier, serveur, titre de session. */
function hintText(h: ImputationHints | undefined): string {
  if (!h) return "";
  const parts = [
    ...(h.roots ?? []).map((r) => `dossier ${lastSegment(r)}`),
    ...(h.ssh_hosts ?? []).map((host) => `serveur ${host}`),
    ...(h.session_title ? [`« ${h.session_title} »`] : []),
  ];
  const text = parts.join(" · ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

interface SuggestionGroup {
  key: string;
  suggestion: ProjectSuggestion;
  minutes: number;
  eventIds: string[];
}

interface HintGroup {
  text: string;
  minutes: number;
  count: number;
}

interface Prospect {
  root: string;
  days: number;
}

/** Une activité porte au plus une suggestion ; celles qui proposent le même
 * projet (et lot) se confirment ensemble. Les autres se lisent par leurs
 * indices. Une racine sans projet revenue sur plusieurs jours devient un
 * prospect. */
function readHints(activities: UnassignedActivity[]): {
  suggestions: SuggestionGroup[];
  hints: HintGroup[];
  prospects: Prospect[];
} {
  const suggestions = new Map<string, SuggestionGroup>();
  const hints = new Map<string, HintGroup>();
  const rootDays = new Map<string, Set<string>>();
  for (const a of activities) {
    const sp = a.suggested_project;
    if (sp) {
      const key = `${sp.project_id}:${sp.work_item_id ?? ""}`;
      const g = suggestions.get(key) ?? {
        key,
        suggestion: sp,
        minutes: 0,
        eventIds: [],
      };
      g.minutes += a.worked_minutes;
      if (!g.eventIds.includes(a.event_id)) g.eventIds.push(a.event_id);
      suggestions.set(key, g);
      continue;
    }
    const text = hintText(a.imputation_hints);
    if (!text) continue;
    const h = hints.get(text) ?? { text, minutes: 0, count: 0 };
    h.minutes += a.worked_minutes;
    h.count += 1;
    hints.set(text, h);
    for (const root of a.imputation_hints?.roots ?? []) {
      const days = rootDays.get(root) ?? new Set<string>();
      days.add(a.date);
      rootDays.set(root, days);
    }
  }
  const byMinutes = (x: { minutes: number }, y: { minutes: number }) =>
    y.minutes - x.minutes;
  return {
    suggestions: [...suggestions.values()].sort(byMinutes),
    hints: [...hints.values()].sort(byMinutes),
    prospects: [...rootDays]
      .filter(([, days]) => days.size > 1)
      .map(([root, days]) => ({ root, days: days.size }))
      .sort((x, y) => y.days - x.days),
  };
}

/** Le projet suggéré se confirme d'un geste : une réimputation, unitaire ou
 * groupée, avec le motif par défaut. */
function SuggestionItem({
  group,
  workerUrl,
  editable,
}: {
  group: SuggestionGroup;
  workerUrl: string;
  editable: boolean;
}) {
  const { confirm, pending, error } = useConfirmSuggestion(workerUrl);
  const sp = group.suggestion;
  const label = sp.project_label || "un projet déjà utilisé";

  return (
    <li className="unassigned-hint">
      <span className="text">
        Projet suggéré : {label}, parce que {sp.reason}
        <span className="rng">
          {formatMinutes(group.minutes)}
          {` · ${group.eventIds.length} activité${plural(group.eventIds.length)}`}
        </span>
      </span>
      {editable && (
        <button
          type="button"
          className="pill-btn tap"
          aria-label={`Confirmer ${label}`}
          disabled={pending}
          onClick={() =>
            void confirm({
              suggestion: sp,
              eventIds: group.eventIds,
              minutes: group.minutes,
            })
          }
        >
          {pending ? "Imputation…" : "Confirmer"}
        </button>
      )}
      {error && (
        <p className="reimpute-error" role="alert">
          {error}
        </p>
      )}
    </li>
  );
}

/** Créer le projet d'un dossier qui revient, puis lui rattacher ce dossier
 * par une règle pour soi, appliquée au temps passé. */
function ProspectSheet({
  root,
  workerUrl,
  onClose,
}: {
  root: string;
  workerUrl: string;
  onClose: () => void;
}) {
  const create = useCreateImputationRule();
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);

  async function attach(p: PmProject) {
    onClose();
    try {
      const result = await createRuleAndApply(
        create.mutateAsync,
        {
          worker_url: workerUrl,
          match_field: "root",
          match_value: root,
          project_id: p.id,
          project_label: p.name.slice(0, LABEL_MAX),
        },
        APPLY_WINDOWS,
      );
      invalidateReimputationReads(qc);
      pushToast({
        variant: "info",
        title: `Dossier ${lastSegment(root)} rattaché à ${p.name}`,
        subtitle:
          result.complete && result.applied
            ? `${formatMinutes(result.applied.worked_minutes)} imputées, la suite le sera d'office`
            : "Le temps à venir sera imputé d'office ; le temps passé le sera depuis Règles d'imputation.",
      });
    } catch {
      pushToast({
        variant: "error",
        title: `Projet ${p.name} créé`,
        subtitle: `Le dossier ${lastSegment(root)} n'a pas pu y être rattaché : faites-le depuis Règles d'imputation.`,
      });
    }
  }

  return (
    <ProjectFormSheet
      initialName={lastSegment(root)}
      onClose={onClose}
      onSaved={(p) => void attach(p)}
    />
  );
}

/** Détail du temps sans source : projets suggérés, indices, dossiers qui
 * reviennent sans projet. */
function UnknownSourceDetails({
  activities,
  workerUrl,
  editable,
}: {
  activities: UnassignedActivity[];
  workerUrl: string;
  editable: boolean;
}) {
  const canCreate = useAdminScope("pm:project:create");
  const [prospect, setProspect] = useState<string | null>(null);
  const { suggestions, hints, prospects } = readHints(activities);
  if (suggestions.length + hints.length === 0) return null;

  return (
    <>
      <ul
        className="unassigned-hints"
        aria-label="Pistes pour le temps sans source"
      >
        {suggestions.map((g) => (
          <SuggestionItem
            key={g.key}
            group={g}
            workerUrl={workerUrl}
            editable={editable}
          />
        ))}
        {hints.map((h) => (
          <li key={h.text} className="unassigned-hint">
            <span className="text">
              {h.text}
              <span className="rng">
                {formatMinutes(h.minutes)}
                {` · ${h.count} activité${plural(h.count)}`}
              </span>
            </span>
          </li>
        ))}
        {editable &&
          canCreate &&
          prospects.map((p) => (
            <li key={p.root} className="unassigned-hint">
              <span className="text">
                Le dossier {lastSegment(p.root)} revient sur {p.days} jours sans
                projet
              </span>
              <button
                type="button"
                className="pill-btn secondary tap"
                onClick={() => setProspect(p.root)}
              >
                Créer le projet {lastSegment(p.root)}
              </button>
            </li>
          ))}
      </ul>
      {prospect && (
        <ProspectSheet
          root={prospect}
          workerUrl={workerUrl}
          onClose={() => setProspect(null)}
        />
      )}
    </>
  );
}

/** Une décision par source : le projet choisi devient une règle (pour soi),
 * appliquée tout de suite au temps passé et ensuite dès la réception. Avec
 * `eventIds`, ces créneaux sont réimputés et la règle n'est créée que si
 * l'indice est retenu. */
export function AttachSourceSheet({
  source,
  workerUrl,
  onClose,
  eventIds,
  retain: initialRetain = false,
  createName,
  details,
}: {
  source: AttachTarget;
  workerUrl: string;
  onClose: () => void;
  eventIds?: string[];
  retain?: boolean;
  /** Nom proposé à la création d'un projet. */
  createName?: string;
  /** Indices du temps à rattacher, montrés tels quels. */
  details?: ReactNode;
}) {
  const [retain, setRetain] = useState(initialRetain);
  const reimpute = useReimputeEvents();
  const retainHint = useRetainHint(workerUrl);
  const [projectId, setProjectId] = useState("");
  const [workItemId, setWorkItemId] = useState("");
  const [branchOnly, setBranchOnly] = useState(
    !!source.branch && !TRUNK_BRANCHES.has(source.branch),
  );
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<PmProject | null>(null);
  const formId = useId();
  const projectFieldId = useId();
  const workItemFieldId = useId();
  const projects = useProjects();
  const workItems = useProjectWorkItems(projectId || undefined);
  const create = useCreateImputationRule();
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);
  const canCreate = useAdminScope("pm:project:create");

  const fetched = projects.data?.items ?? [];
  // Le projet tout juste créé reste choisi avant que la liste ne se recharge.
  const projectList =
    created && !fetched.some((p) => p.id === created.id)
      ? [...fetched, created]
      : fetched;
  const itemList = workItems.data?.items ?? [];
  const pending = create.isPending || applying || reimpute.pending;
  const domain = source.kind === "domain";
  const field =
    source.field ??
    (domain
      ? "domain"
      : source.kind === "code"
        ? ruleFieldOf(source.value)
        : undefined);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (pending || !projectId) return;
    const project = projectList.find((p) => p.id === projectId);
    const workItem = itemList.find((w) => w.id === workItemId);
    const label = [project?.name, workItem?.title].filter(Boolean).join(" / ");
    const ref: ProjectRef = {
      project_id: projectId,
      ...(workItem ? { work_item_id: workItem.id } : {}),
      ...(label ? { project_label: label.slice(0, LABEL_MAX) } : {}),
    };
    setError(null);
    if (eventIds) {
      try {
        await reimpute.run(eventIds, ref);
      } catch (err) {
        setError(reimputeErrorMessage(err));
        return;
      }
      if (retain && field) {
        setApplying(true);
        await retainHint(
          {
            field,
            value: source.value,
            ...(branchOnly && source.branch ? { branch: source.branch } : {}),
          },
          ref,
        );
        setApplying(false);
      }
      invalidateReimputationReads(qc);
      pushToast({
        variant: "info",
        title: `${formatMinutes(source.minutes)} imputées à ${label || "ce projet"}`,
      });
      onClose();
      return;
    }
    setApplying(true);
    let result: RuleApplied;
    try {
      result = await createRuleAndApply(
        create.mutateAsync,
        {
          worker_url: workerUrl,
          match_field: field ?? ruleFieldOf(source.value),
          match_value: source.value,
          ...(branchOnly && source.branch
            ? { branch_pattern: source.branch }
            : {}),
          ...ref,
        },
        APPLY_WINDOWS,
      );
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? "Ce rattachement existe déjà : le temps restant vient d'une feuille déjà soumise."
          : "Rattachement impossible pour le moment. Réessayez dans un instant.",
      );
      return;
    } finally {
      setApplying(false);
    }
    invalidateReimputationReads(qc);
    pushToast({
      variant: "info",
      title: `${sourceTitle(source)} rattaché à ${label || "ce projet"}`,
      subtitle:
        result.complete && result.applied
          ? `${formatMinutes(result.applied.worked_minutes)} imputées, la suite le sera d'office`
          : "Le temps à venir sera imputé d'office ; le temps passé le sera depuis Règles d'imputation.",
    });
    onClose();
  }

  if (creating) {
    return (
      <ProjectFormSheet
        {...(createName ? { initialName: createName } : {})}
        onClose={() => setCreating(false)}
        onSaved={(p) => {
          setCreated(p);
          setProjectId(p.id);
          setWorkItemId("");
          setCreating(false);
        }}
      />
    );
  }

  return (
    <Sheet
      title="Rattacher à un projet"
      onClose={onClose}
      footer={
        <div className="sheet-cta">
          <button
            type="submit"
            form={formId}
            className="primary"
            disabled={pending || !projectId}
          >
            {pending ? "Rattachement…" : "Rattacher"}
          </button>
        </div>
      }
    >
      <p className="reimpute-target">
        {sourceTitle(source)}
        {source.branch && ` · branche ${source.branch}`} ·{" "}
        {formatMinutes(source.minutes)} sans projet
      </p>
      {details && <p className="reimpute-clues">{details}</p>}
      {eventIds ? (
        <p className="form-hint">
          {retain && field
            ? "Ces créneaux iront à ce projet, et Chronos s'en souviendra pour le temps à venir."
            : "Ces créneaux iront à ce projet."}
        </p>
      ) : (
        <p className="form-hint">
          Tout le temps passé {domain ? "sur ce site" : "dans ce dépôt"} ira à
          ce projet, celui déjà pointé comme celui à venir. Une feuille déjà
          soumise ne bouge pas.
        </p>
      )}
      <form
        id={formId}
        className="inline-form reimpute-form"
        onSubmit={(e) => void handleSubmit(e)}
      >
        <label htmlFor={projectFieldId}>
          Projet
          <select
            id={projectFieldId}
            value={projectId}
            onChange={(e) => {
              setProjectId(e.target.value);
              setWorkItemId("");
            }}
            required
          >
            <option value="">
              {projects.isLoading
                ? "Chargement des projets…"
                : projects.isError
                  ? "Projets indisponibles"
                  : "Choisir un projet"}
            </option>
            {projectList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        {canCreate && (
          <button
            type="button"
            className="pill-btn ghost"
            onClick={() => setCreating(true)}
          >
            {createName ? `Créer le projet ${createName}` : "Nouveau projet"}
          </button>
        )}
        <label htmlFor={workItemFieldId}>
          Lot (facultatif)
          <select
            id={workItemFieldId}
            value={workItemId}
            onChange={(e) => setWorkItemId(e.target.value)}
            disabled={!projectId}
          >
            <option value="">Projet entier</option>
            {itemList.map((w) => (
              <option key={w.id} value={w.id}>
                {w.title}
              </option>
            ))}
          </select>
        </label>
        {source.branch && (!eventIds || retain) && (
          <label className="check-row">
            <input
              type="checkbox"
              checked={branchOnly}
              onChange={(e) => setBranchOnly(e.target.checked)}
            />
            Seulement la branche {source.branch}
          </label>
        )}
        {eventIds && field && (
          <label className="check-row">
            <input
              type="checkbox"
              checked={retain}
              onChange={(e) => setRetain(e.target.checked)}
            />
            Retenir pour {sourceTitle(source)}
          </label>
        )}
        {error && (
          <p className="reimpute-error" role="alert">
            {error}
          </p>
        )}
      </form>
    </Sheet>
  );
}

/** Temps sans projet d'une période, rangé par source : une ligne par dépôt
 * (et branche) ou par site, rattachée en une fois. Le temps sans source
 * s'impute d'un bloc. Sans `editable`, lecture seule. */
export function UnassignedSources({
  workerUrl,
  from,
  to,
  editable,
}: {
  workerUrl: string;
  from: string;
  to: string;
  editable: boolean;
}) {
  const { data, isLoading, isError } = useUnassignedTime(workerUrl, from, to);
  const titleId = useId();
  const [attach, setAttach] = useState<Source | null>(null);
  const [targets, setTargets] = useState<ReimputeTarget[] | null>(null);
  const claimable = claimableActivities(data?.activities ?? []);
  const sources = sourcesOf(claimable);
  const totalMin = claimable.reduce((sum, a) => sum + a.worked_minutes, 0);

  if (!isLoading && !isError && sources.length === 0) return null;

  return (
    <section className="unassigned-sources" aria-labelledby={titleId}>
      <h2 id={titleId} className="section-label">
        À rattacher
        {totalMin > 0 && (
          <span className="total">{formatMinutes(totalMin)}</span>
        )}
      </h2>
      {isError ? (
        <p className="unassigned-empty">Temps sans projet indisponible</p>
      ) : isLoading ? (
        <p className="unassigned-empty">Chargement…</p>
      ) : (
        <>
          <p className="unassigned-help">
            {editable
              ? "Le kit ne sait pas à quel projet va ce temps. Choisissez-le une fois par dépôt ou par site : Chronos s'en souvient."
              : "La feuille est déjà soumise : ce temps reste sans projet."}
          </p>
          <ul className="time-blocks" aria-label="Temps sans projet par source">
            {sources.map((s) => (
              <li key={s.key} className="unassigned">
                <span className="text">
                  <span className="prj">{sourceTitle(s)}</span>
                  <span className="rng">
                    {formatMinutes(s.minutes)}
                    {s.branch && ` · branche ${s.branch}`}
                    {` · ${s.eventIds.length} activité${plural(s.eventIds.length)}`}
                  </span>
                </span>
                {editable &&
                  (s.kind === "none" ? (
                    <button
                      type="button"
                      className="pill-btn secondary tap"
                      aria-label={`Imputer ${sourceTitle(s)}`}
                      onClick={() =>
                        setTargets(
                          s.eventIds.slice(0, MAX_BATCH).map((eventId) => ({
                            eventId,
                            label: `${formatMinutes(s.minutes)} sans source connue`,
                          })),
                        )
                      }
                    >
                      Imputer
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="pill-btn tap"
                      aria-label={`Rattacher ${sourceTitle(s)}`}
                      onClick={() => setAttach(s)}
                    >
                      Rattacher
                    </button>
                  ))}
                {s.kind === "none" && (
                  <UnknownSourceDetails
                    activities={s.activities}
                    workerUrl={workerUrl}
                    editable={editable}
                  />
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {attach && (
        <AttachSourceSheet
          source={attach}
          workerUrl={workerUrl}
          onClose={() => setAttach(null)}
        />
      )}
      {targets && (
        <ReimputeSheet targets={targets} onClose={() => setTargets(null)} />
      )}
    </section>
  );
}

export default UnassignedSources;
